import React from 'react';
import { Box, Container, Typography, Grid, Card, CardContent } from '@mui/material';
import InventoryIcon from '@mui/icons-material/Inventory2Outlined';
import PointOfSaleIcon from '@mui/icons-material/PointOfSaleOutlined';
import AnalyticsIcon from '@mui/icons-material/AnalyticsOutlined';
import BookOnlineIcon from '@mui/icons-material/BookOnlineOutlined';
import StoreIcon from '@mui/icons-material/StorefrontOutlined';
import GroupsIcon from '@mui/icons-material/Groups3Outlined';
import { landingColors } from '../../../theme/landingTheme';

const features = [
  {
    icon: InventoryIcon,
    title: 'Real-time Inventory',
    description:
      'Track stock levels across all locations in real-time. Get alerts for low stock and automate reordering.',
    color: '#6366F1',
    gradient: 'linear-gradient(135deg, #6366F1 0%, #8B5CF6 100%)',
  },
  {
    icon: PointOfSaleIcon,
    title: 'Point of Sale',
    description:
      'Lightning-fast POS system with barcode scanning, receipt printing, and seamless payment processing.',
    color: '#10B981',
    gradient: 'linear-gradient(135deg, #10B981 0%, #059669 100%)',
  },
  {
    icon: AnalyticsIcon,
    title: 'Smart Analytics',
    description:
      'Data-driven insights with beautiful dashboards. Understand sales trends, customer behavior, and growth.',
    color: '#F59E0B',
    gradient: 'linear-gradient(135deg, #F59E0B 0%, #D97706 100%)',
  },
  {
    icon: BookOnlineIcon,
    title: 'Reservations',
    description:
      'Let customers reserve products for pickup. Manage appointments and reduce no-shows with reminders.',
    color: '#EC4899',
    gradient: 'linear-gradient(135deg, #EC4899 0%, #DB2777 100%)',
  },
  {
    icon: StoreIcon,
    title: 'Multi-location',
    description:
      'Manage multiple stores from a single dashboard. Transfer stock, compare performance, and scale easily.',
    color: '#3B82F6',
    gradient: 'linear-gradient(135deg, #3B82F6 0%, #2563EB 100%)',
  },
  {
    icon: GroupsIcon,
    title: 'B2B Portal',
    description:
      'Wholesale partner management with custom pricing tiers, quotes, invoices, and bulk ordering.',
    color: '#8B5CF6',
    gradient: 'linear-gradient(135deg, #8B5CF6 0%, #7C3AED 100%)',
  },
];

export const FeaturesSection: React.FC = () => {
  return (
    <Box
      id="features"
      component="section"
      sx={{
        py: { xs: 10, md: 16 },
        position: 'relative',
        backgroundColor: landingColors.surface,
      }}
    >
      {/* Background decoration */}
      <Box
        sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          height: 200,
          background: `linear-gradient(180deg, ${landingColors.background} 0%, transparent 100%)`,
          pointerEvents: 'none',
        }}
      />

      <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
        {/* Section Header */}
        <Box
          sx={{
            textAlign: 'center',
            maxWidth: 640,
            mx: 'auto',
            mb: { xs: 6, md: 10 },
          }}
        >
          <Typography
            variant="overline"
            sx={{
              display: 'inline-block',
              mb: 2,
              px: 2,
              py: 0.5,
              backgroundColor: landingColors.primaryMuted,
              borderRadius: '6px',
            }}
          >
            Features
          </Typography>
          <Typography variant="h2" sx={{ mb: 3 }}>
            Everything You Need to{' '}
            <Box
              component="span"
              sx={{
                position: 'relative',
                '&::after': {
                  content: '""',
                  position: 'absolute',
                  bottom: 4,
                  left: 0,
                  right: 0,
                  height: 12,
                  backgroundColor: landingColors.primaryMuted,
                  zIndex: -1,
                  borderRadius: '4px',
                },
              }}
            >
              Run Your Store
            </Box>
          </Typography>
          <Typography
            variant="body1"
            sx={{
              fontSize: { xs: '1rem', md: '1.125rem' },
              color: landingColors.textSecondary,
            }}
          >
            Powerful features designed for modern retail. From inventory management to
            B2B partnerships, we've got you covered.
          </Typography>
        </Box>

        {/* Features Grid */}
        <Grid container spacing={3}>
          {features.map((feature, index) => {
            const Icon = feature.icon;
            return (
              <Grid item xs={12} sm={6} md={4} key={index}>
                <Card
                  elevation={0}
                  sx={{
                    height: '100%',
                    border: `1px solid ${landingColors.border}`,
                    borderRadius: '16px',
                    transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                    cursor: 'default',
                    position: 'relative',
                    overflow: 'hidden',
                    '&::before': {
                      content: '""',
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      right: 0,
                      height: 4,
                      background: feature.gradient,
                      opacity: 0,
                      transition: 'opacity 0.3s ease',
                    },
                    '&:hover': {
                      transform: 'translateY(-8px)',
                      boxShadow: `0 24px 48px rgba(15, 23, 42, 0.1)`,
                      borderColor: `${feature.color}30`,
                      '&::before': {
                        opacity: 1,
                      },
                      '& .feature-icon': {
                        transform: 'scale(1.1) rotate(-5deg)',
                      },
                    },
                  }}
                >
                  <CardContent sx={{ p: 4 }}>
                    {/* Icon */}
                    <Box
                      className="feature-icon"
                      sx={{
                        width: 56,
                        height: 56,
                        borderRadius: '14px',
                        background: `${feature.color}12`,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        mb: 3,
                        transition: 'transform 0.3s ease',
                      }}
                    >
                      <Icon
                        sx={{
                          fontSize: 28,
                          color: feature.color,
                        }}
                      />
                    </Box>

                    {/* Title */}
                    <Typography
                      variant="h5"
                      sx={{
                        mb: 1.5,
                        fontWeight: 600,
                        color: landingColors.textPrimary,
                      }}
                    >
                      {feature.title}
                    </Typography>

                    {/* Description */}
                    <Typography
                      variant="body2"
                      sx={{
                        color: landingColors.textSecondary,
                        lineHeight: 1.7,
                      }}
                    >
                      {feature.description}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            );
          })}
        </Grid>

        {/* Bottom decoration */}
        <Box
          sx={{
            mt: 10,
            display: 'flex',
            justifyContent: 'center',
            gap: 1,
          }}
        >
          {[...Array(5)].map((_, i) => (
            <Box
              key={i}
              sx={{
                width: i === 2 ? 32 : 8,
                height: 8,
                borderRadius: '4px',
                backgroundColor:
                  i === 2 ? landingColors.primary : landingColors.border,
                transition: 'all 0.3s ease',
              }}
            />
          ))}
        </Box>
      </Container>
    </Box>
  );
};

export default FeaturesSection;
